/**
 * My Team panels for the Apex Team page.
 *
 * - MyTeamStatus: transfer-allowance bar for signed-in users (saved team
 *   carries forward race to race; shows live transfer usage and the -10 pt
 *   penalty per extra transfer) or a sign-in banner for anonymous users.
 * - MyTeamResults: post-race summary of the user's saved team — fantasy
 *   points per asset (with captain multipliers) and actual price changes.
 */

import { CheckCircle2, Infinity as InfinityIcon, Users } from "lucide-react";
import { SignInToSave } from "@/features/vault/SignInToSave";
import { m } from "@/paraglide/messages";
import type {
  TeamCalculatorConstructor,
  TeamCalculatorDriver,
} from "../hooks/use-f1-data";
import type { SavedRaceTeam, TransferSummary } from "../lib/my-team";
import { getTeamColor } from "../lib/team-colors";

interface MyTeamStatusProps {
  signedIn: boolean;
  /** null until a race is selected and the vault has loaded. */
  summary: TransferSummary | null;
  /** Race name the line-up was carried forward from (no snapshot for the
   * current race yet); null when the current race has its own snapshot or
   * there is nothing to carry. */
  carriedFromRaceName: string | null;
  /** Whether any asset is currently selected (controls the saved hint). */
  hasSelection: boolean;
}

export function MyTeamStatus({
  signedIn,
  summary,
  carriedFromRaceName,
  hasSelection,
}: MyTeamStatusProps) {
  if (!signedIn) {
    return (
      <SignInToSave
        redirectUrl="/f1-fantasy/apex-team"
        title={m.f1_myteam_signin_title()}
        description={m.f1_myteam_signin_desc()}
        ctaLabel={m.f1_myteam_signin_cta()}
      />
    );
  }
  if (!summary) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 p-3 rounded-xl border border-border bg-card/50">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-primary" aria-hidden="true" />
        <span className="text-sm font-medium text-foreground">
          {m.f1_myteam_title()}
        </span>
      </div>

      {summary.mode === "first-race" && (
        <span className="text-xs text-muted-foreground">
          {m.f1_myteam_first_race()}
        </span>
      )}
      {summary.mode === "chip-unlimited" && (
        <span className="flex items-center gap-1 text-xs text-accent">
          <InfinityIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {m.f1_myteam_unlimited_chip()}
        </span>
      )}
      {summary.mode === "counted" && (
        <>
          <span
            className={`font-mono text-xs ${
              summary.extraTransfers > 0
                ? "text-destructive"
                : "text-foreground"
            }`}
          >
            {m.f1_myteam_transfers_status({
              used: summary.transfersUsed,
              free: summary.freeTransfers,
            })}
            {summary.carriedOver === 1 ? ` (${m.f1_myteam_carry_note()})` : ""}
          </span>
          {summary.penaltyPoints > 0 && (
            <span className="px-2 py-0.5 rounded-md bg-destructive/15 text-destructive font-mono text-xs font-medium">
              {m.f1_myteam_penalty({ points: summary.penaltyPoints })}
            </span>
          )}
        </>
      )}

      {carriedFromRaceName && (
        <span className="text-xs text-muted-foreground">
          {m.f1_myteam_carried_forward({ race: carriedFromRaceName })}
        </span>
      )}

      {hasSelection && (
        <span className="flex items-center gap-1 text-xs text-success ml-auto">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          {m.f1_myteam_saved()}
        </span>
      )}
    </div>
  );
}

interface MyTeamResultsProps {
  snapshot: SavedRaceTeam;
  drivers: TeamCalculatorDriver[];
  constructors: TeamCalculatorConstructor[];
  penaltyPoints: number;
  raceName: string;
}

interface ResultRow {
  key: string;
  name: string;
  color: string;
  multiplier: 1 | 2 | 3;
  points: number | null;
  priceChange: number | null;
}

export function MyTeamResults({
  snapshot,
  drivers,
  constructors,
  penaltyPoints,
  raceName,
}: MyTeamResultsProps) {
  const hasX3 = snapshot.chip === "x3";
  const noNeg = snapshot.chip === "No Neg";

  const rows: ResultRow[] = [];
  snapshot.drivers.forEach((slug, slotIndex) => {
    if (!slug) return;
    const d = drivers.find((x) => x.driverSlug === slug);
    if (!d) return;
    const multiplier: 1 | 2 | 3 =
      hasX3 && slotIndex === 0 ? 3 : slotIndex === (hasX3 ? 1 : 0) ? 2 : 1;
    rows.push({
      key: `d-${slug}`,
      name: d.fullName,
      color: getTeamColor(d.constructorSlug),
      multiplier,
      points: d.currentRacePoints ?? null,
      priceChange: d.actualPriceChange ?? null,
    });
  });
  snapshot.constructors.forEach((slug) => {
    if (!slug) return;
    const c = constructors.find((x) => x.slug === slug);
    if (!c) return;
    rows.push({
      key: `c-${slug}`,
      name: c.name,
      color: getTeamColor(c.slug),
      multiplier: 1,
      points: c.currentRacePoints ?? null,
      priceChange: c.actualPriceChange ?? null,
    });
  });

  if (rows.length === 0) return null;

  const effectivePoints = (row: ResultRow): number => {
    if (row.points === null) return 0;
    // "No Neg" caps a negative score at zero (approximation: the official
    // 2026 rule applies per scoring category; we only have asset totals).
    const base = noNeg ? Math.max(0, row.points) : row.points;
    return base * row.multiplier;
  };

  const totalPoints =
    rows.reduce((sum, row) => sum + effectivePoints(row), 0) - penaltyPoints;
  const totalPriceChange = rows.reduce(
    (sum, row) => sum + (row.priceChange ?? 0),
    0,
  );

  return (
    <div className="p-4 rounded-xl border border-border bg-card/50">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
        <h2 className="font-display font-semibold text-foreground">
          {m.f1_myteam_results_title({ race: raceName })}
        </h2>
        <span className="font-mono text-sm font-medium text-foreground">
          {totalPoints > 0 ? "+" : ""}
          {totalPoints.toFixed(0)} {m.f1_myteam_results_points()}
        </span>
        <span
          className={`font-mono text-sm font-medium ${
            totalPriceChange > 0.05
              ? "text-success"
              : totalPriceChange < -0.05
                ? "text-destructive"
                : "text-muted-foreground"
          }`}
        >
          {totalPriceChange > 0 ? "+" : ""}${totalPriceChange.toFixed(1)}M{" "}
          {m.f1_myteam_results_value()}
        </span>
        {penaltyPoints > 0 && (
          <span className="font-mono text-xs text-destructive">
            {m.f1_myteam_penalty({ points: penaltyPoints })}
          </span>
        )}
      </div>
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center gap-2 text-sm">
            <span
              className="w-1 h-5 rounded-full shrink-0"
              style={{ backgroundColor: row.color }}
              aria-hidden="true"
            />
            <span className="flex-1 min-w-0 truncate text-foreground">
              {row.name}
              {row.multiplier > 1 && (
                <span className="ml-1 text-xs text-accent font-mono">
                  ×{row.multiplier}
                </span>
              )}
            </span>
            <span className="font-mono text-xs w-14 text-right text-foreground">
              {row.points === null
                ? "—"
                : `${effectivePoints(row) > 0 ? "+" : ""}${effectivePoints(row).toFixed(0)}`}
            </span>
            <span
              className={`font-mono text-xs w-14 text-right ${
                row.priceChange === null || Math.abs(row.priceChange) < 0.05
                  ? "text-muted-foreground"
                  : row.priceChange > 0
                    ? "text-success"
                    : "text-destructive"
              }`}
            >
              {row.priceChange === null
                ? "—"
                : `${row.priceChange > 0 ? "+" : ""}${row.priceChange.toFixed(1)}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
